import type { StatusAluno } from '@arena/shared/dominio';
import { partesNoFuso } from '@arena/shared/tempo';
import type { Situacao } from '../ui/primitivos.js';

/**
 * Os textos de alunos. Funções puras — nenhuma toca a rede nem o DOM.
 *
 * `telefoneFormatado` e `dataCurta` repetem o par que já existe em
 * `agenda/formatos.ts`: são generalistas o bastante (dígitos de telefone,
 * "AAAA-MM-DD" → "DD/MM") para não valer importar de um módulo de
 * domínio para o outro só por isso — a mesma razão que mantém
 * `@arena/shared` como o lugar de quem é usado nos dois lados, e não
 * aqui, que é só o frontend.
 */

/** "62999991180" → "(62) 9 9999-1180". O que vier fora do padrão passa como está. */
export function telefoneFormatado(digitos: string | null): string {
  if (!digitos) return '—';
  if (digitos.length === 11) {
    return `(${digitos.slice(0, 2)}) ${digitos[2]} ${digitos.slice(3, 7)}-${digitos.slice(7)}`;
  }
  if (digitos.length === 10) {
    return `(${digitos.slice(0, 2)}) ${digitos.slice(2, 6)}-${digitos.slice(6)}`;
  }
  return digitos;
}

/** "16/03". */
export function dataCurta(data: string): string {
  return `${data.slice(8, 10)}/${data.slice(5, 7)}`;
}

/** "16/03/2026". */
export function dataCompleta(data: string): string {
  return `${data.slice(8, 10)}/${data.slice(5, 7)}/${data.slice(0, 4)}`;
}

/**
 * "12 de março de 2026" — o subtítulo de `#m-aluno` ("Ativa desde…").
 *
 * Lida ao meio-dia UTC de propósito: é uma data de calendário, não um
 * instante, e ao meio-dia UTC nenhum fuso do planeta a empurra para o
 * dia vizinho.
 */
export function dataPorExtenso(data: string): string {
  return new Intl.DateTimeFormat('pt-BR', {
    timeZone: 'UTC',
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(new Date(`${data}T12:00:00Z`));
}

/**
 * "12/03/2026 às 19:41" — o instante de um aceite ou de uma transição,
 * NO FUSO DA ARENA. Nunca `toLocaleString()` sem fuso: um aceite às
 * 21h30 em Goiânia não pode aparecer como do dia seguinte só porque o
 * navegador de quem olha está em Lisboa.
 */
export function instanteCompleto(iso: string, fuso: string): string {
  const partes = partesNoFuso(iso, fuso);
  return `${dataCompleta(partes.data)} às ${partes.hora}`;
}

export function idadeTexto(idade: number | null, menorDeIdade: boolean): string | null {
  if (idade === null) return null;
  return `${idade} ${idade === 1 ? 'ano' : 'anos'}${menorDeIdade ? ' · menor de idade' : ''}`;
}

/** A cor do selo de status — o mesmo vocabulário do KPI de `#v-alunos`. */
export const SITUACAO_DO_STATUS: Readonly<Record<StatusAluno, Situacao>> = {
  Ativo: 'ok',
  Experimental: 'aguardando',
  Inativo: 'atencao',
  Cancelado: 'neutra',
};

/**
 * O primeiro erro de cada campo, pelo caminho do zod ("responsavelFinanceiro").
 * Tipo estrutural: o front não depende do zod diretamente — ele chega
 * pelos schemas de @arena/shared.
 */
export function errosPorCampo(
  issues: readonly { path: readonly (string | number)[]; message: string }[],
): Record<string, string> {
  const erros: Record<string, string> = {};
  for (const issue of issues) {
    const campo = issue.path.join('.') || '_';
    erros[campo] ??= issue.message;
  }
  return erros;
}

/** O corpo de erro que o servidor devolve — `{ campo: [mensagens] }`. */
export function primeiraMensagem(campos: Record<string, string[]>): Record<string, string> {
  const erros: Record<string, string> = {};
  for (const [campo, mensagens] of Object.entries(campos)) {
    if (mensagens[0]) erros[campo] = mensagens[0];
  }
  return erros;
}
